import React, { useEffect } from "react";

import { RequestStatuses, RequestTypes } from "@assets/constants";
import { useAppDispatch } from "@assets/hooks";
import {
  DateRangeOptions,
  FilterElementType,
  FilterProvider,
  FilterSectionType,
} from "@samskip/frontend-components";
import { getUserProfileFromAPI } from "@state/slices/userSlice";
import { addWeeks, subMonths } from "date-fns";
import { useTranslation } from "react-i18next";

import Requests from "./requests";

const RequestsFilterProvider = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getUserProfileFromAPI());
  }, []);

  const translateCustomRangeOptions = (option: DateRangeOptions): string => {
    switch (option) {
      case DateRangeOptions.LAST_WEEK: {
        return t("LABEL_LAST_WEEK");
      }
      case DateRangeOptions.LAST_TWO_WEEKS: {
        return t("LABEL_LAST_TWO_WEEKS");
      }
      case DateRangeOptions.LAST_MONTH: {
        return t("LABEL_LAST_MONTH");
      }
      case DateRangeOptions.LAST_THREE_MONTHS: {
        return t("LABEL_LAST_THREE_MONTHS");
      }
      case DateRangeOptions.CUSTOM_DATE: {
        return t("LABEL_CUSTOM_RANGE");
      }
      default:
        return "";
    }
  };

  const requestFiltersSections: FilterSectionType[] = [
    {
      title: t("LABEL_TYPE"),
      id: "types",
      type: FilterElementType.CHECKBOX,
      values: [
        {
          label: t("LABEL_DELIVERY"),
          value: RequestTypes.Delivery,
        },
        {
          label: t("LABEL_BOOKING_CORRECTION"),
          value: RequestTypes.BookingCorrection,
        },
        {
          label: t("LABEL_GENERAL_INQUIRY"),
          value: RequestTypes.GeneralInquiry,
        },
        {
          label: t("LABEL_CUSTOMSCLEARED"),
          value: RequestTypes.CustomsClearance,
        },
      ],
    },
    {
      title: t("LABEL_STATUS"),
      id: "statuses",
      type: FilterElementType.CHECKBOX,
      values: [
        {
          label: t("LABEL_PREREGISTERED"),
          value: RequestStatuses.PreRegistered,
        },
        {
          label: t("LABEL_REGISTERED"),
          value: RequestStatuses.Registered,
        },
        {
          label: t("LABEL_ON_HOLD"),
          value: RequestStatuses.OnHold,
        },
        {
          label: t("LABEL_IN_PROGRESS"),
          value: RequestStatuses.InProgress,
        },
        {
          label: t("LABEL_COMPLETED"),
          value: RequestStatuses.Completed,
        },
        {
          label: t("LABEL_CANCELED"),
          value: RequestStatuses.Canceled,
        },
        {
          label: t("LABEL_CREATED"),
          value: RequestStatuses.Created,
        },
      ],
    },
    {
      title: t("LABEL_CREATED"),
      id: "createdDate",
      type: FilterElementType.DATEPICKER,
      defaultValue: DateRangeOptions.LAST_WEEK,
      props: {
        translateCustomRangeOptions: translateCustomRangeOptions,
        availableRanges: [
          DateRangeOptions.LAST_WEEK,
          DateRangeOptions.LAST_TWO_WEEKS,
          DateRangeOptions.LAST_MONTH,
          DateRangeOptions.LAST_THREE_MONTHS,
        ],
        placeholder: t("PLACEHOLDER_SELECT_DATE"),
        maxDate: addWeeks(new Date(), 2),
        minDate: subMonths(new Date(), 3),
      },
    },
    {
      title: t("LABEL_ONLY_MY_REQUESTS"),
      id: "areOnlyMyRequests",
      type: FilterElementType.TOGGLE_SWITCH,
      defaultValue: false,
    },
  ];

  return (
    <FilterProvider sections={requestFiltersSections}>
      <Requests />
    </FilterProvider>
  );
};

export default RequestsFilterProvider;
